import {
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Checkbox } from "./Checkbox";
import { cx } from "./cx";
import { EmptyState } from "./EmptyState";
import { IconChevronDown } from "./icons";
import { headerState, selectRange, toggleAll, toggleId } from "./selectionModel";
import { Skeleton } from "./Skeleton";
import "./Table.css";

export type SortDirection = "asc" | "desc";

export interface TableSort {
  key: string;
  dir: SortDirection;
}

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  /** Cell content; defaults to `String(row[key])`. */
  render?: (row: T, index: number) => ReactNode;
  /** CSS width, e.g. "8rem" or "20%". */
  width?: string;
  align?: "start" | "center" | "end";
  /** Monospace cell (IDs, counts). */
  mono?: boolean;
  /** Header becomes a sort button; sorting itself is done by the caller. */
  sortable?: boolean;
  /** Hide below 40rem viewport width. */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  columns: readonly TableColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  /** Accessible name of the table ("Mappings"). */
  caption: string;
  /** Show the checkbox column. */
  selectable?: boolean;
  selectedIds?: ReadonlySet<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /** Return false to disable a row's checkbox. */
  isRowSelectable?: (row: T) => boolean;
  /** No rows + loading → skeleton rows. Rows + loading → stale rows stay, dimmed. */
  loading?: boolean;
  skeletonRows?: number;
  /** Rendered when there are no rows and not loading. */
  empty?: ReactNode;
  /** Mouse convenience; keep a real link/button in a cell for keyboard users. */
  onRowClick?: (row: T) => void;
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  /** Max height of the scroll area; enables the sticky header. */
  maxHeight?: string;
  rowClassName?: (row: T) => string | undefined;
  className?: string;
}

const INTERACTIVE = "a, button, input, select, textarea, label, [role='button'], [role='switch']";

function defaultCell<T>(row: T, key: string): ReactNode {
  const v = (row as Record<string, unknown>)[key];
  if (v === null || v === undefined) return "—";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

/**
 * Dense data table: sticky header, checkbox selection (shift-click ranges, Esc clears),
 * skeleton loading, empty state and caller-driven sorting.
 */
export function Table<T>({
  columns,
  rows,
  getRowId,
  caption,
  selectable = false,
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  loading = false,
  skeletonRows = 8,
  empty,
  onRowClick,
  sort,
  onSortChange,
  maxHeight,
  rowClassName,
  className,
}: TableProps<T>) {
  const selected = selectedIds ?? new Set<string>();
  const anchor = useRef<string | null>(null);
  const selectableIds = rows
    .filter((r) => !isRowSelectable || isRowSelectable(r))
    .map((r) => getRowId(r));
  const head = headerState(selected, selectableIds);
  const colCount = columns.length + (selectable ? 1 : 0);
  const showSkeleton = loading && rows.length === 0;

  const emit = (next: Set<string>) => onSelectionChange?.(next);

  const onRowCheck = (id: string, checked: boolean, e: ChangeEvent<HTMLInputElement>) => {
    const shift = (e.nativeEvent as unknown as { shiftKey?: boolean }).shiftKey === true;
    if (shift && anchor.current) emit(selectRange(selected, selectableIds, anchor.current, id, checked));
    else emit(toggleId(selected, id));
    anchor.current = id;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && selectable && selected.size > 0) {
      e.stopPropagation();
      emit(new Set());
    }
  };

  const onRowClickInternal = (row: T, e: MouseEvent<HTMLTableRowElement>) => {
    if (!onRowClick) return;
    if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
    onRowClick(row);
  };

  const sortButton = (col: TableColumn<T>) => {
    const active = sort?.key === col.key;
    const nextDir: SortDirection = active && sort?.dir === "asc" ? "desc" : "asc";
    return (
      <button
        type="button"
        className={cx("ui-table__sort", active && "ui-table__sort--active")}
        onClick={() => onSortChange?.({ key: col.key, dir: nextDir })}
      >
        {col.header}
        <IconChevronDown
          className={cx(
            "ui-table__sort-icon",
            active && sort?.dir === "asc" && "ui-table__sort-icon--asc",
          )}
        />
      </button>
    );
  };

  return (
    <div
      className={cx("ui-table-wrap", loading && rows.length > 0 && "ui-table-wrap--stale", className)}
      style={maxHeight ? { maxHeight } : undefined}
      onKeyDown={onKeyDown}
    >
      <table className="ui-table" aria-label={caption} aria-busy={loading || undefined}>
        <thead>
          <tr>
            {selectable ? (
              <th scope="col" className="ui-table__check">
                <Checkbox
                  aria-label={head === "all" ? "Deselect all rows" : "Select all rows"}
                  checked={head === "all"}
                  indeterminate={head === "some"}
                  disabled={selectableIds.length === 0 || showSkeleton}
                  onChange={() => emit(toggleAll(selected, selectableIds))}
                />
              </th>
            ) : null}
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={col.width ? { width: col.width } : undefined}
                className={cx(
                  `ui-table__cell--${col.align ?? "start"}`,
                  col.hideOnMobile && "ui-table__cell--hide-mobile",
                )}
                aria-sort={
                  col.sortable
                    ? sort?.key === col.key
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                    : undefined
                }
              >
                {col.sortable ? sortButton(col) : col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showSkeleton
            ? Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={`sk-${i}`} className="ui-table__skeleton-row" aria-hidden="true">
                  {selectable ? (
                    <td className="ui-table__check">
                      <Skeleton variant="rect" width="var(--space-4)" height="var(--space-4)" />
                    </td>
                  ) : null}
                  {columns.map((col, c) => (
                    <td
                      key={col.key}
                      className={cx(col.hideOnMobile && "ui-table__cell--hide-mobile")}
                    >
                      <Skeleton width={`${55 + ((i * 7 + c * 13) % 40)}%`} />
                    </td>
                  ))}
                </tr>
              ))
            : null}
          {!loading && rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="ui-table__empty">
                {empty ?? <EmptyState compact title="No rows to show." />}
              </td>
            </tr>
          ) : null}
          {rows.map((row, index) => {
            const id = getRowId(row);
            const isSelected = selected.has(id);
            const canSelect = !isRowSelectable || isRowSelectable(row);
            return (
              <tr
                key={id}
                data-selected={selectable ? isSelected : undefined}
                className={cx(
                  isSelected && "ui-table__row--selected",
                  onRowClick && "ui-table__row--clickable",
                  rowClassName?.(row),
                )}
                onClick={onRowClick ? (e) => onRowClickInternal(row, e) : undefined}
              >
                {selectable ? (
                  <td className="ui-table__check">
                    <Checkbox
                      aria-label={`Select row ${index + 1}`}
                      checked={isSelected}
                      disabled={!canSelect}
                      onChange={(checked, e) => onRowCheck(id, checked, e)}
                    />
                  </td>
                ) : null}
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cx(
                      `ui-table__cell--${col.align ?? "start"}`,
                      col.mono && "ui-table__cell--mono",
                      col.hideOnMobile && "ui-table__cell--hide-mobile",
                    )}
                  >
                    {col.render ? col.render(row, index) : defaultCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
